import { toRaw } from 'vue'

import { useNotifications } from '@/composables/useNotifications'
import { t } from '@/i18n'
import type { DataphoneConfig } from '@/types/config'
import { CustomError } from '@/utils/custom-error'
import { isElectron } from '@/utils/device-type'

import { type IDataphone } from './dataphone'

export type RedsysChargeResponse = {
  redsys: {
    rtsId: string
    orderNumber: string
  }
  receipt: {
    clientCard: string
    orderNumber: string
    expiration: string
    commerce: string
    resCode: string
    terminal: string
    transactionNum: string
    tagApp: string
    idApp: string
    resVerification: string
    resCodeAuto: string
  }
}

export class RedsysDataphone implements IDataphone {
  public initialized: boolean = false
  public canCancel: boolean = false
  public showWaitingScreen: boolean = true

  private initializing: boolean = false
  private config: DataphoneConfig
  private ipcRenderer: any

  constructor(config: DataphoneConfig) {
    this.initialized = false
    this.initializing = false
    this.config = config
  }

  async init() {
    if (!isElectron) return
    const { ipcRenderer } = window.require('electron')
    this.ipcRenderer = ipcRenderer
    if (!this.initializing) {
      this.initializing = true
      const { notifyError, close } = useNotifications()
      try {
        await this.ipcRenderer.invoke('dataphone:init', toRaw(this.config))
        this.initialized = true
        close('dataphone-init-error')
      } catch (error: any) {
        notifyError({
          id: 'dataphone-init-error',
          title: t('dataphone.bad-config-title'),
          description: error.message ? error.message : t('dataphone.bad-config-subtitle')
        })
      }
    }
    this.initializing = false
  }

  async charge({
    amount,
    paymentId
  }: {
    amount: number
    paymentId: string
  }): Promise<RedsysChargeResponse> {
    if (!this.initialized) {
      throw new CustomError('NOT_INITIALIZED', t('checkout.dataphone.not-initialized'))
    }
    const res = await this.ipcRenderer.invoke('dataphone:charge', amount, paymentId)
    if (!res?.chargeAuthorized) {
      throw new CustomError('NOT_AUTHORIZED', t('checkout.dataphone.not-authorized'))
    }
    return res?.metadata
  }

  cancel(): Promise<void> {
    if (!this.initialized) {
      throw new CustomError('NOT_INITIALIZED', t('checkout.dataphone.not-initialized'))
    }
    return this.ipcRenderer.invoke('dataphone:cancel')
  }

  async refund(payment: any) {
    if (!this.initialized) {
      throw new CustomError('NOT_INITIALIZED', t('checkout.dataphone.not-initialized'))
    }
    const res = await this.ipcRenderer.invoke('dataphone:refund', toRaw(payment))
    return res
  }
}
